/**
 * Roofing material brands we install. Text badges only until logo use is approved.
 * Optional `logoSrc` is reserved for future permissioned brand artwork.
 */

export type BrandCategoryId = "asphalt" | "flat" | "metal" | "skylights" | "specialty";

export type Brand = {
  id: string;
  name: string;
  /** When true, show a BC-Made tag on the about page. */
  bcMade?: boolean;
  /** Show on the homepage horizontal strip. */
  featuredOnHome?: boolean;
  /** Optional path for a future approved logo asset. */
  logoSrc?: string;
  category: BrandCategoryId;
};

export type BrandCategory = {
  id: BrandCategoryId;
  title: string;
  body: string;
};

export const brandCategories: BrandCategory[] = [
  {
    id: "asphalt",
    title: "Asphalt shingles",
    body: "Architectural laminate systems suited to most Metro Vancouver sloped roofs, with product lines selected for coastal moisture, moss pressure and local exposure.",
  },
  {
    id: "flat",
    title: "Flat and low-slope membranes",
    body: "Modified bitumen and single-ply systems specified for drainage, transitions and the detailing that decides long-term performance on low-slope assemblies.",
  },
  {
    id: "metal",
    title: "Metal roofing",
    body: "Standing-seam and related metal profiles for homes where longevity, crisp lines and coastal weather resistance are the priority.",
  },
  {
    id: "skylights",
    title: "Skylights",
    body: "Factory skylight systems installed with flashing kits and transitions matched to the roof covering - not improvised curb work.",
  },
  {
    id: "specialty",
    title: "Specialty and BC-made products",
    body: "Regional manufacturers for metal, accessories and related building products when the assembly calls for them.",
  },
];

export const brands: Brand[] = [
  { id: "malarkey", name: "Malarkey", category: "asphalt", featuredOnHome: true },
  { id: "gaf", name: "GAF", category: "asphalt", featuredOnHome: true },
  { id: "owens-corning", name: "Owens Corning", category: "asphalt", featuredOnHome: true },
  { id: "iko", name: "IKO", category: "asphalt", featuredOnHome: true },
  { id: "bp-canada", name: "BP Canada", category: "asphalt", featuredOnHome: true },
  { id: "certainteed", name: "CertainTeed", category: "asphalt", featuredOnHome: true },
  { id: "soprema", name: "Soprema", category: "flat", featuredOnHome: true },
  {
    id: "iko-commercial",
    name: "IKO Commercial",
    category: "flat",
  },
  {
    id: "westform",
    name: "Westform Metals",
    category: "metal",
    featuredOnHome: true,
    bcMade: true,
  },
  { id: "velux", name: "VELUX", category: "skylights", featuredOnHome: true },
  {
    id: "interlock",
    name: "Interlock",
    category: "specialty",
    bcMade: true,
  },
  {
    id: "waldun",
    name: "Waldun",
    category: "specialty",
    bcMade: true,
  },
];

export const homeBrands = brands.filter((brand) => brand.featuredOnHome);

export const brandTrademarkDisclaimer =
  "Brand names are trademarks of their respective owners. Listing a brand means Revolin Roofing installs that manufacturer’s products when specified for a project; it does not imply endorsement, partnership or certification unless separately stated.";
